import React, { useState } from "react";
import { Calendar, Trash2 } from "lucide-react";
import { colors } from "@/lib/theme";
import { Field, Input, Textarea, PrimaryButton, Sheet } from "@/components/peeps/ui";
import { LocationField } from "@/components/peeps/LocationField";

export function EventFormSheet({ visible, mode, initial, googleSynced, onClose, onSubmit, onDelete, submitting }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [date, setDate] = useState(initial?.date || "");
  const [time, setTime] = useState(initial?.time || "");
  const [endTime, setEndTime] = useState(initial?.end_time || "");
  const [location, setLocation] = useState(initial?.location || "");
  const [link, setLink] = useState(initial?.link || "");
  const [details, setDetails] = useState(initial?.details || "");
  const [notify, setNotify] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const canSubmit = !!title.trim() && !!date && !!time;
  const isEdit = mode === "edit";

  const submit = () => {
    let l = link.trim();
    if (l && !/^https?:\/\//i.test(l)) l = `https://${l}`;
    onSubmit({ title: title.trim(), date, time, end_time: endTime || "", location: location.trim(), link: l, details: details.trim() }, notify);
  };

  return (
    <Sheet visible={visible} onClose={onClose} title={isEdit ? "Edit plan" : "Add a plan"} testID="event-form-sheet">
      <Field label="Title">
        <Input testID="event-title-input" placeholder="Dinner, hike, game night…" value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>

      <div className="flex gap-3 w-full">
        <div className="flex-1 min-w-0">
          <Field label="Date">
            <Input testID="event-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ colorScheme: "dark", maxWidth: "100%" }} className="w-full min-w-0" />
          </Field>
        </div>
        <div className="flex-1 min-w-0">
          <Field label="Start time">
            <Input testID="event-time-input" type="time" value={time} onChange={(e) => setTime(e.target.value)} style={{ colorScheme: "dark", maxWidth: "100%" }} className="w-full min-w-0" />
          </Field>
        </div>
      </div>

      <Field label="End time (optional)">
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <Input testID="event-end-time-input" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} style={{ colorScheme: "dark", maxWidth: "100%" }} className="w-full min-w-0" />
          </div>
          {!!endTime && (
            <button type="button" data-testid="clear-end-time" onClick={() => setEndTime("")} className="font-display text-[12.5px] shrink-0" style={{ color: colors.onSurfaceTertiary }}>Clear</button>
          )}
        </div>
      </Field>

      <Field label="Location">
        <LocationField value={location} onChange={setLocation} testID="event-location-input" />
      </Field>

      <Field label="Link">
        <Input testID="event-link-input" placeholder="Tickets, menu, maps…" value={link} onChange={(e) => setLink(e.target.value)} />
      </Field>

      <Field label="Details">
        <Textarea testID="event-details-input" placeholder="Anything people should know" value={details} onChange={(e) => setDetails(e.target.value)} />
      </Field>

      {!isEdit && (
        <button data-testid="notify-toggle" onClick={() => setNotify(!notify)} className="flex items-center gap-3 mb-3 w-full text-left">
          <span className="relative inline-flex items-center rounded-full transition-colors" style={{ width: 42, height: 24, background: notify ? colors.brand : colors.surfaceTertiary }}>
            <span className="absolute rounded-full bg-white transition-transform" style={{ width: 18, height: 18, top: 3, left: 3, transform: notify ? "translateX(18px)" : "translateX(0)" }} />
          </span>
          <span className="flex-1 font-display text-[13.5px]" style={{ color: colors.onSurfaceSecondary }}>Notify the group when this is added</span>
        </button>
      )}

      <div className="flex items-center gap-1.5 mb-4">
        <Calendar size={13} color={googleSynced ? colors.brandSecondary : colors.faint} />
        <span className="font-display font-medium text-[12.5px]" style={{ color: googleSynced ? colors.brandSecondary : colors.faint }}>
          {googleSynced ? "Will sync to this group's Google Calendar" : "Not synced — turn on Google Calendar in settings"}
        </span>
      </div>

      <PrimaryButton testID="event-submit-button" label={isEdit ? "Save changes" : "Add to calendar"} onClick={submit} disabled={!canSubmit} loading={submitting} />

      {isEdit && onDelete && (
        <div className="mt-2">
          {confirmDelete ? (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-display font-medium text-[13px]" style={{ color: colors.onSurfaceTertiary }}>Delete this plan?</span>
              <button data-testid="confirm-delete-event" onClick={onDelete} className="rounded-md py-1.5 px-3 font-display font-bold text-[12.5px]" style={{ background: colors.error, color: "#450A0A" }}>Yes, delete</button>
              <button onClick={() => setConfirmDelete(false)} className="font-display font-medium text-[12.5px]" style={{ color: colors.onSurfaceTertiary }}>Cancel</button>
            </div>
          ) : (
            <button data-testid="delete-event-button" onClick={() => setConfirmDelete(true)} className="flex items-center justify-center gap-1.5 w-full rounded-xl py-3" style={{ border: "1px solid #3D2530" }}>
              <Trash2 size={15} color={colors.error} />
              <span className="font-display font-medium text-[13.5px]" style={{ color: colors.error }}>Delete plan</span>
            </button>
          )}
        </div>
      )}
    </Sheet>
  );
}
